/**
 * Service level objectives (ADR-0041, S1): five operations under a service.
 *
 * What the UI must know that the types do not say (register §5 risk 10):
 * `window_days` defaults to 28 and is sent explicitly; `SloUpdate` has no
 * `environment` or `sli` because both are immutable after creation
 * (decision S1-5), so the form disables them rather than letting the server
 * refuse with 400 `slo_invalid`. Status fields are read as the contract
 * defines them: `coverage`, `budget_consumed` and `budget_remaining` are
 * shares (0.35), `sli` and `target` are percentages (99.9).
 */

import { expect, expectNoContent, http } from './client';
import type { components } from './gen/schema';

type Schemas = components['schemas'];

export type Slo = Schemas['Slo'];
export type SloCreate = Schemas['SloCreate'];
export type SloUpdate = Schemas['SloUpdate'];
export type SloStatus = Schemas['SloStatus'];
export type SloState = SloStatus['state'];
export type SloBurnRates = Schemas['SloBurnRates'];
export type Sli = Schemas['Sli'];
export type SloQuery = Schemas['SloQuery'];
export type SloMatcher = Schemas['SloMatcher'];
export type SloWindowDays = Slo['window_days'];

export const SLO_DEFAULT_WINDOW_DAYS: SloWindowDays = 28;
export const SLO_WINDOW_OPTIONS: SloWindowDays[] = [7, 28, 30];
/** The contract's bounds on an SLI: one `good` or 1–5 `bad`, ≤10 extra matchers each. */
export const SLO_MAX_BAD_QUERIES = 5;
export const SLO_MAX_MATCHERS = 10;
export const SLO_TARGET_MIN = 90;
export const SLO_TARGET_MAX = 99.999;

export interface SloPage<T> {
  data: T[];
  next_cursor: string | null;
}

function paged<T>(value: { data?: unknown; next_cursor?: unknown }): SloPage<T> {
  return {
    data: (value.data ?? []) as T[],
    next_cursor: typeof value.next_cursor === 'string' ? value.next_cursor : null,
  };
}

const servicePath = (orgId: string, serviceId: string) => ({
  org_id: orgId,
  service_id: serviceId,
});
const sloPath = (orgId: string, serviceId: string, sloId: string) => ({
  ...servicePath(orgId, serviceId),
  slo_id: sloId,
});

export async function listServiceSLOs(
  orgId: string,
  serviceId: string,
  query: { limit?: number; cursor?: string } = {},
): Promise<SloPage<Slo>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/services/{service_id}/slos', {
      params: { path: servicePath(orgId, serviceId), query },
    }),
  );
  return paged<Slo>(value);
}

export function createServiceSLO(orgId: string, serviceId: string, body: SloCreate): Promise<Slo> {
  return expect(
    http.POST('/organizations/{org_id}/services/{service_id}/slos', {
      params: { path: servicePath(orgId, serviceId) },
      body,
    }),
  );
}

export function getServiceSLO(orgId: string, serviceId: string, sloId: string): Promise<Slo> {
  return expect(
    http.GET('/organizations/{org_id}/services/{service_id}/slos/{slo_id}', {
      params: { path: sloPath(orgId, serviceId, sloId) },
    }),
  );
}

export function updateServiceSLO(
  orgId: string,
  serviceId: string,
  sloId: string,
  body: SloUpdate,
): Promise<Slo> {
  return expect(
    http.PATCH('/organizations/{org_id}/services/{service_id}/slos/{slo_id}', {
      params: { path: sloPath(orgId, serviceId, sloId) },
      body,
    }),
  );
}

export function deleteServiceSLO(
  orgId: string,
  serviceId: string,
  sloId: string,
  version?: number,
): Promise<void> {
  return expectNoContent(
    http.DELETE('/organizations/{org_id}/services/{service_id}/slos/{slo_id}', {
      params: {
        path: sloPath(orgId, serviceId, sloId),
        query: version === undefined ? {} : { if_version: version },
      },
    }),
  );
}

export type SloBucket = Schemas['SloBucket'];

/**
 * The evaluated buckets of one SLO, newest first as the server pages them
 * (DL-242 item 6). The caller reverses for a chart.
 */
export async function listServiceSLOBuckets(
  orgId: string,
  serviceId: string,
  sloId: string,
  query: { since?: string; until?: string; limit?: number; cursor?: string } = {},
): Promise<SloPage<SloBucket>> {
  const value = await expect(
    http.GET('/organizations/{org_id}/services/{service_id}/slos/{slo_id}/buckets', {
      params: { path: sloPath(orgId, serviceId, sloId), query },
    }),
  );
  return paged<SloBucket>(value);
}

/** Optional operations the UI checks before drawing a panel that needs them. */
const OPTIONAL_OPERATIONS = {
  listServiceSLOBuckets: true,
} as const;

export type OptionalOperationId = keyof typeof OPTIONAL_OPERATIONS;

/** Whether the generated client carries an operation the UI can use optionally. */
export function hasOperation(id: OptionalOperationId): boolean {
  return OPTIONAL_OPERATIONS[id];
}
